import React from 'react';
import PropTypes from 'prop-types';

import SourceDataButton, {
  pickFields,
  FieldSource,
  FooterSourceToggle,
  useFooterSource,
} from './ChartSourceModal';
import {
  WIDTH,
  HEIGHT,
  CX,
  CY,
  OUTER_R,
  INNER_R,
  parseLocaleNumber,
  polarToXY,
  segmentIndexToAngle,
  arcPath,
  needlePath,
  formatPercentage,
  isBlankFooterValue,
} from './gaugeGeometry';

/**
 * Gewichtstoeslag gauge.
 *
 * Five fixed bands:
 *   -0,105..-0,03 | -0,03..-0,01 | -0,01..0 | 0 | 0..0,05
 *
 * The fourth band is a single point rather than a range: it holds the price
 * classes whose gewichtstoeslag is exactly zero.
 *
 * Each price class is placed by its own average gewichtstoeslag (field 286) and
 * contributes its share of the delivery (field 275) to that band; classes
 * landing in the same band add together.
 *
 * A band's arc width is its share of the delivery, so the dial shows the
 * distribution rather than a fixed scale. A band holding nothing shrinks to
 * GEWTSL_MIN_FRACTION so it stays visible and its edge label stays readable.
 *
 * Field 275 is the backend's already-rounded whole-number percentage, so the
 * shares can overshoot 100. They are scaled back to total 100 when that
 * happens, since a gauge cannot be more than full; an undershoot is left alone.
 * The spekdikte gauge counts rows instead to force a 100% total, at the cost of
 * weighting a 2-animal class the same as a 54-animal one.
 *
 * The needles: red from klassement_totalen 246 (Gem. Gew tsl), the same field
 * the footer's Totaal row shows; black from the BPG benchmark (drgmbmk DEGEMGT,
 * week -1, DELTMCE = ALL). Where the webservice sends no DEGEMGT the black
 * needle and its footer row simply do not render.
 */

// klassement key 286 = 'Gem. Waarden - Gew tsl'; 275 = '%'.
const GEWTSL_KEY = '286';
const SHARE_KEY = '275';

// Inner band edges. The zero band is a point, so zero appears twice: once as
// the top of the -0,01..0 band and once as the bottom of the 0..0,05 band.
export const GEWTSL_MIN = -0.105;
export const GEWTSL_MAX = 0.05;
export const GEWTSL_EDGES = [GEWTSL_MIN, -0.03, -0.01, 0, 0, GEWTSL_MAX];
export const GEWTSL_SEGMENT_COUNT = GEWTSL_EDGES.length - 1;

// The second zero is left bare - it would print '0' twice side by side.
export const GEWTSL_LABELS = ['-0,03', '-0,01', '0', null, '0,05'];
export const GEWTSL_START_LABEL = '-0,105';

// Smallest slice of the arc a section may occupy. Sized so that four empty
// sections in a row still leave their band edge labels far enough apart to read.
export const GEWTSL_MIN_FRACTION = 0.05;

// The green 0..0,05 section is the one growers are aiming for, so when it leads
// the delivery it takes exactly half the dial and the other four share the rest
// in proportion to each other. Half is a fixed share, not a floor: a delivery
// that is almost entirely green would otherwise swallow the whole arc and leave
// the other bands unreadable. If some other section holds more than green, this
// does not apply at all and green takes only the share it earned.
export const GEWTSL_GREEN_FRACTION = 0.5;

// A deduction is bad and a supplement is good, so this runs red to green,
// reusing the palette values already on this page.
export const GEWTSL_COLORS = [
  '#8B0000', '#e07070', '#f0c840', '#F2DCBB', '#2d6a2d',
];

/**
 * Which band an average gewichtstoeslag falls into.
 *
 * Bands are lower-inclusive / upper-exclusive, except the zero band, which
 * takes exactly zero and nothing else. Values off either end of the scale fold
 * into the end bands so no share is silently dropped.
 */
export function getGewichtstoeslagSegmentIndex(value) {
  if (value < GEWTSL_EDGES[1]) return 0;
  if (value < GEWTSL_EDGES[2]) return 1;
  if (value < 0) return 2;
  if (value === 0) return 3;
  return 4;
}

/**
 * Where a needle value sits along the dial, as a fractional segment index in
 * [0, 5]: the integer part is the band, the fraction how far through it the
 * value sits. gewichtstoeslagNeedlePosition turns that into an arc position
 * using the band's actual drawn width.
 *
 * Exactly zero centres on the zero band rather than sitting on one of its
 * edges, since that band is a single point.
 */
export function gewichtstoeslagNeedleIndex(value) {
  const v = Math.min(GEWTSL_MAX, Math.max(GEWTSL_MIN, value));

  if (v < GEWTSL_EDGES[1]) {
    return (v - GEWTSL_MIN) / (GEWTSL_EDGES[1] - GEWTSL_MIN);
  }
  if (v < GEWTSL_EDGES[2]) {
    return 1 + (v - GEWTSL_EDGES[1]) / (GEWTSL_EDGES[2] - GEWTSL_EDGES[1]);
  }
  if (v < 0) {
    return 2 + (v - GEWTSL_EDGES[2]) / (0 - GEWTSL_EDGES[2]);
  }
  if (v === 0) {
    return 3.5;
  }
  return 4 + v / GEWTSL_MAX;
}

/**
 * Fold the klassement into the five bands.
 *
 * klassement[i]['286'] = that price class's average gewichtstoeslag, which
 *                        places it in a band
 * klassement[i]['275'] = that price class's percentage of the delivery
 *
 * Rows with no 286 cannot be placed and are skipped.
 */
export function buildGewichtstoeslagSegments(klassement) {
  const shares = Array(GEWTSL_SEGMENT_COUNT).fill(0);

  if (Array.isArray(klassement)) {
    klassement.forEach((item) => {
      const toeslag = parseLocaleNumber(item[GEWTSL_KEY], null);
      if (toeslag == null) {
        return;
      }
      shares[getGewichtstoeslagSegmentIndex(toeslag)] +=
        parseLocaleNumber(item[SHARE_KEY], 0);
    });
  }

  const percentages = capAtFull(shares);
  const fractions = arcFractions(shares);
  let cursor = 0;

  return Array.from({ length: GEWTSL_SEGMENT_COUNT }, (_, i) => {
    const start = cursor;
    cursor += fractions[i];
    return {
      min: GEWTSL_EDGES[i],
      max: GEWTSL_EDGES[i + 1],
      color: GEWTSL_COLORS[i],
      label: GEWTSL_LABELS[i],
      percentage: percentages[i],
      fraction: fractions[i],
      start,
    };
  });
}

/**
 * The five bands at a fixed size, for a page that has no klassement to size
 * them by. The four real ranges share the dial equally; the zero band is a
 * single value, so it only gets the minimum slice - equal shares would give
 * "exactly 0" a fifth of the dial.
 */
export function buildFixedGewichtstoeslagSegments() {
  const zero = GEWTSL_SEGMENT_COUNT - 2;
  const range = (1 - GEWTSL_MIN_FRACTION) / (GEWTSL_SEGMENT_COUNT - 1);
  let cursor = 0;

  return Array.from({ length: GEWTSL_SEGMENT_COUNT }, (_, i) => {
    const fraction = i === zero ? GEWTSL_MIN_FRACTION : range;
    const start = cursor;
    cursor += fraction;
    return {
      min: GEWTSL_EDGES[i],
      max: GEWTSL_EDGES[i + 1],
      color: GEWTSL_COLORS[i],
      label: GEWTSL_LABELS[i],
      percentage: 0,
      fraction,
      start,
    };
  });
}

/**
 * A gauge cannot be more than full.
 *
 * Field 275 is the backend's percentage already rounded to a whole number, so
 * the shares can add up to a little over 100 - 101% shows up in real
 * deliveries. When they overshoot, they are scaled back so the total reads 100
 * and no single section can exceed it. An undershoot is left as it is.
 */
function capAtFull(shares) {
  const total = shares.reduce((acc, s) => acc + s, 0);

  if (total <= 100) {
    return shares.slice();
  }
  return shares.map((s) => s * (100 / total));
}

/**
 * Share out a budget of arc between sections, in proportion to what they hold.
 *
 * A section holding nothing would otherwise vanish, taking its band edge label
 * with it, so anything that would fall below minFraction of the whole dial is
 * widened to it and the remainder is split proportionally between the sections
 * above it. Those therefore keep their exact proportions relative to each other.
 */
function distribute(shares, budget, minFraction) {
  const count = shares.length;
  const total = shares.reduce((acc, s) => acc + s, 0);

  // Nothing to go on: split the budget evenly rather than collapse the sections.
  if (total <= 0) {
    return shares.map(() => budget / count);
  }

  const proportions = shares.map((s) => s / total);
  const isSmall = proportions.map((p) => p * budget < minFraction);
  const smallCount = isSmall.filter(Boolean).length;

  // Everything is a sliver: nothing left to distribute, so split evenly.
  if (smallCount === count) {
    return shares.map(() => budget / count);
  }

  const remaining = budget - smallCount * minFraction;
  const largeTotal = proportions.reduce(
    (acc, p, i) => (isSmall[i] ? acc : acc + p),
    0
  );

  return proportions.map((p, i) =>
    isSmall[i] ? minFraction : (p / largeTotal) * remaining
  );
}

/**
 * How much of the arc each section gets.
 *
 * Sections are sized by their share of the delivery, except that the green
 * section takes exactly GEWTSL_GREEN_FRACTION of the dial whenever it is the
 * largest, with the other four sharing what is left in proportion to each
 * other. When another section holds more than green, that does not apply and
 * every section is sized by its share.
 */
function arcFractions(shares) {
  const total = shares.reduce((acc, s) => acc + s, 0);

  if (total <= 0) {
    return shares.map(() => 1 / GEWTSL_SEGMENT_COUNT);
  }

  const green = GEWTSL_SEGMENT_COUNT - 1;
  const greenLeads = shares.every((s, i) => i === green || s <= shares[green]);

  if (!greenLeads) {
    return distribute(shares, 1, GEWTSL_MIN_FRACTION);
  }

  const others = distribute(
    shares.slice(0, green),
    1 - GEWTSL_GREEN_FRACTION,
    GEWTSL_MIN_FRACTION
  );
  return others.concat([GEWTSL_GREEN_FRACTION]);
}

/**
 * Where a needle value sits along the arc, as a fraction in [0, 1].
 *
 * The value picks its section, then interpolates across that section's arc
 * width - so the needle always points inside the band its value belongs to,
 * however wide or narrow the delivery has made that band.
 */
export function gewichtstoeslagNeedlePosition(value, segments) {
  const index = gewichtstoeslagNeedleIndex(value);
  const i = Math.min(GEWTSL_SEGMENT_COUNT - 1, Math.floor(index));
  const seg = segments[i];

  return seg.start + (index - i) * seg.fraction;
}

// Distinct from the other gauges' filter so the page has no duplicate DOM ids.
const SHADOW_FILTER_ID = 'gewichtstoeslag-needle-shadow';
const LABEL_OFFSET = 22;
const NEEDLE_RED = '#dc2626';
const NEEDLE_BLACK = '#000';

function needleAngle(value, segments) {
  return positionToAngle(gewichtstoeslagNeedlePosition(value, segments));
}

// A fraction of the arc, 0 at the left end and 1 at the right, as an angle.
function positionToAngle(position) {
  return segmentIndexToAngle(position * GEWTSL_SEGMENT_COUNT, GEWTSL_SEGMENT_COUNT);
}

function GewichtstoeslagGauge({ segments, zValue, bpgValue, showShares = true }) {
  const startLabelPos = polarToXY(positionToAngle(0), OUTER_R + LABEL_OFFSET);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role='img'
      aria-label='Gewichtstoeslag gauge'
    >
      <defs>
        <filter id={SHADOW_FILTER_ID} x='-20%' y='-20%' width='140%' height='140%'>
          <feDropShadow dx='1' dy='1' stdDeviation='2' floodOpacity='0.3' />
        </filter>
      </defs>

      {/* The low end of the scale, which no segment's upper edge carries */}
      <text
        x={startLabelPos.x}
        y={startLabelPos.y}
        textAnchor='middle'
        fontSize='11'
        fontWeight='bold'
        fill='#000'
      >
        {GEWTSL_START_LABEL}
      </text>

      {segments.map((seg, i) => {
        const a1 = positionToAngle(seg.start);
        const a2 = positionToAngle(seg.start + seg.fraction);
        const mid = polarToXY((a1 + a2) / 2, (OUTER_R + INNER_R) / 2);
        const labelPos = polarToXY(a2, OUTER_R + LABEL_OFFSET);

        return (
          <g key={i}>
            <path
              data-segment={i}
              d={arcPath(a1, a2, OUTER_R, INNER_R)}
              fill={seg.color}
              stroke='white'
              strokeWidth='0.8'
            />

            {/* Band edge label; the repeated zero edge is left bare */}
            {seg.label != null && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor='middle'
                fontSize='11'
                fontWeight='bold'
                fill='#000'
              >
                {seg.label}
              </text>
            )}

            {/* Share of the delivery in this band; 0% is shown, not hidden */}
            {showShares && (
              <text
                data-share
                x={mid.x}
                y={mid.y + 5}
                textAnchor='middle'
                fontSize='12'
                fontWeight='600'
                fill='#fff'
                pointerEvents='none'
              >
                {formatPercentage(seg.percentage)}
              </text>
            )}
          </g>
        );
      })}

      {/* Red needle: this delivery's average gewichtstoeslag (field 246) */}
      {zValue != null && (
        <path
          d={needlePath(needleAngle(zValue, segments), INNER_R - 10, 5)}
          fill={NEEDLE_RED}
          filter={`url(#${SHADOW_FILTER_ID})`}
        />
      )}

      {/* Black needle: BPG benchmark (drgmbmk DEGEMGT, week -1, ALL) */}
      {bpgValue != null && (
        <path
          d={needlePath(needleAngle(bpgValue, segments), ((INNER_R - 10) * 3) / 4, 5)}
          fill={NEEDLE_BLACK}
          filter={`url(#${SHADOW_FILTER_ID})`}
        />
      )}

      <circle cx={CX} cy={CY} r={10} fill='#888' />
      <circle cx={CX} cy={CY} r={5} fill='#fff' />
    </svg>
  );
}

/**
 * A toeslag is a few hundredths of a euro, so it needs four decimals - the one
 * decimal a weight gets would round 0,0180 down to 0,0.
 */
function formatToeslag(value) {
  return value.toFixed(4).replace('.', ',');
}

// Shown for a field the webservice did not send at all.
const NOT_SENT = '\u2014';

/**
 * The value as the backend sends it, so the footer reads exactly as specified.
 *
 * A field that is absent reads as an em dash rather than a formatted zero, so
 * "the webservice did not send this" is distinguishable from "the webservice
 * sent zero" while the toeslag field wiring is being confirmed.
 */
function footerValue(raw) {
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim();
  }
  return NOT_SENT;
}

function GewichtstoeslagFooter({ rows }) {
  // In development and staging every row is shown, so the field wiring can be
  // checked against the payload; elsewhere a row with no value is dropped.
  const source = useFooterSource();
  const visibleRows = source.available
    ? rows
    : rows.filter((row) => !isBlankFooterValue(row.value));

  return (
    <div style={{ fontSize: 13, color: '#555', lineHeight: '1.8', textAlign: 'center' }}>
      {visibleRows.map((row) => (
        <div
          key={row.label}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          {row.color ? (
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2,
                background: row.color,
              }}
            />
          ) : (
            <span style={{ display: 'inline-block', width: 10 }} />
          )}
          <span>{`${row.label} (AVG: ${row.value})`}</span>
          <FieldSource source={row.source} shown={source.shown} />
        </div>
      ))}
      <FooterSourceToggle
        available={source.available}
        shown={source.shown}
        onToggle={source.toggle}
      />
    </div>
  );
}

/**
 * The gauge without its footer or shares, for a page that only has two averages
 * to show. A needle whose value is missing is not drawn.
 */
export function GewichtstoeslagAverageGauge({ redValue, blackValue }) {
  return (
    <GewichtstoeslagGauge
      segments={buildFixedGewichtstoeslagSegments()}
      zValue={redValue}
      bpgValue={blackValue}
      showShares={false}
    />
  );
}

/**
 * Gewichtstoeslag panel: gauge plus footer.
 *
 * The footer reads the Gem. Gew tsl family, so it is scaled the same way as the
 * dial: Totaal 246, Z 196, M 226, B 516 and P 546.
 *
 * Z read 546 as well until the client corrected it to 196. That matches
 * fieldLabels.js, where 196 is 'Z - Gem. Gew tsl', and the Z field of every
 * other family (186 gewicht, 191 spekdikte). P keeps 546, the same base+300
 * block the other gauges use for P (531 gewicht, 536 spekdikte).
 */
function GewichtstoeslagChartPanel({ data, bpgValue, title }) {
  const segments = buildGewichtstoeslagSegments(data ? data.klassement : null);
  const kt = data && data.klassement_totalen;

  // Red needle: field 246, Gem. Gew tsl.
  const zValue = parseLocaleNumber(kt && kt['246']);

  // Every gauge lists its rows in the same order: Z, M, B, P, Totaal, BPG.
  const rows = [
    { label: 'Z', source: '196', value: footerValue(kt && kt['196']), color: null },
    { label: 'M', source: '226', value: footerValue(kt && kt['226']), color: null },
    { label: 'B', source: '516', value: footerValue(kt && kt['516']), color: null },
    { label: 'P', source: '546', value: footerValue(kt && kt['546']), color: null },
    { label: 'Totaal', source: '246', value: footerValue(kt && kt['246']), color: NEEDLE_RED },
  ];

  if (bpgValue != null) {
    rows.push({ label: 'BPG', source: 'BPG benchmark (drgmbmk DEGEMGT, week -1, ALL)', value: formatToeslag(bpgValue), color: NEEDLE_BLACK });
  }

  // The payload slice this gauge reads, for the source-data dialog.
  const source = {
    klassement: data ? data.klassement : null,
    klassement_totalen: pickFields(kt, ['246', '196', '546', '516', '226']),
    benchmark: { source: 'drgmbmk', field: 'DEGEMGT', deltmce: 'ALL', value: bpgValue },
  };

  return (
    <div className='statistics-chart-panel'>
      <div className='statistics-chart-header'>
        <h3>{title}</h3>
        <SourceDataButton title={title} source={source} />
      </div>
      <GewichtstoeslagGauge segments={segments} zValue={zValue} bpgValue={bpgValue} />
      <GewichtstoeslagFooter rows={rows} />
    </div>
  );
}

GewichtstoeslagChartPanel.propTypes = {
  data: PropTypes.object,
  bpgValue: PropTypes.number,
  title: PropTypes.string,
};

GewichtstoeslagChartPanel.defaultProps = {
  data: null,
  bpgValue: null,
  title: 'Gewichtstoeslag',
};

export default GewichtstoeslagChartPanel;
